import React, { useMemo } from 'react'
import { View, ActivityIndicator, ViewStyle } from 'react-native'
import { StyleService, useStyleSheet } from '@src/style/service'

export interface ListFooterProps {
  finishedLoadingComponent?: React.ReactNode
  isLoadingMore: boolean
  isListEmpty: boolean
  footerStyles?: ViewStyle
}

export const ListFooter = ({
  isLoadingMore,
  isListEmpty,
  finishedLoadingComponent,
  footerStyles = {},
}: ListFooterProps) => {
  const styles = useStyleSheet(themedStyle)

  const loadingComponentMarginTop = useMemo(
    () => ({ marginTop: finishedLoadingComponent ? 24 : 10 }),
    [finishedLoadingComponent],
  )

  const loadingIndicator = useMemo(
    () => (
      <View style={[styles.footerLoader, loadingComponentMarginTop, footerStyles]}>
        <ActivityIndicator animating size="large" {...styles.loaderColor} />
      </View>
    ),
    [styles.footerLoader, styles.loaderColor, loadingComponentMarginTop, footerStyles],
  )

  const finishedLoadingComponentMemo = useMemo(() => {
    if (!finishedLoadingComponent) {
      return null
    }
    return (
      <View style={[styles.footer, loadingComponentMarginTop, footerStyles]}>
        {finishedLoadingComponent}
      </View>
    )
  }, [finishedLoadingComponent, styles.footer, loadingComponentMarginTop, footerStyles])

  if (isLoadingMore) {
    return loadingIndicator
  }

  if (isListEmpty) {
    return null
  }

  return finishedLoadingComponentMemo ?? null
}

const themedStyle = StyleService.create({
  footerLoader: {
    height: 80,
    paddingVertical: 10,
    marginTop: 10,
    marginBottom: 30,
  },
  loaderColor: {
    color: 'theme.surface.base2',
  },
  footer: {
    backgroundColor: 'theme.background',
    marginBottom: 30,
    height: 80, // to ensure consistent height with loader
  },
})
